import { apiClient } from '@/lib/api/client'
import type {
  ReportProfile,
  ReportVersionView,
  ReportView,
  ValidationIssue,
  VersionInfo,
  ReportProjectRef,
} from '@/lib/types/report'

// Re-export for convenience
export type { ReportProjectRef }

export interface ReportListItem {
  report_id: string
  title: string
  profile_id: string
  status: string
  version: number
  created_at: string
  updated_at: string | null
  projects: ReportProjectRef[]
}

export const reportAPI = {
  listReports: () =>
    apiClient.get<{ count: number; reports: ReportListItem[] }>(`/reports?limit=100`),
  getReport: (id: string) => apiClient.get<ReportView>(`/reports/${id}`),
  getRenderedReport: (id: string) =>
    apiClient.get<{ markdown: string; version: number }>(`/reports/${id}/rendered`),
  getReportVersions: (id: string) =>
    apiClient.get<{ current: number; versions: VersionInfo[] }>(`/reports/${id}/versions`),
  getReportVersionView: (id: string, version: number) =>
    apiClient.get<ReportVersionView>(`/reports/${id}/versions/${version}/view`),
  createReport: (body: { profile_id: string; project_refs: string[]; title?: string; params?: Record<string, unknown> }) =>
    apiClient.post<{ report_id: string; status: string; version: number }>(`/reports`, body),
  deleteReport: (id: string) =>
    apiClient.delete<{ deleted: boolean; report_id: string }>(`/reports/${id}`),
  shareReport: (id: string) =>
    apiClient.post<{ token: string; report_id: string; created_at: string }>(`/reports/${id}/share`),
  revokeShare: (id: string) =>
    apiClient.post<{ revoked: boolean }>(`/reports/${id}/share/revoke`),
  finalizeReport: (id: string) =>
    apiClient.post<{ status: string; version: number }>(`/reports/${id}/finalize`),
  validateReport: (id: string) =>
    apiClient.post<{ issue_count: number; version: number }>(`/reports/${id}/validate`),
  getReportIssues: (id: string) =>
    apiClient.get<{ actionable: ValidationIssue[]; superseded: ValidationIssue[] }>(`/reports/${id}/issues`),
  renameReport: (id: string, title: string) =>
    apiClient.patch<{ report_id: string; title: string }>(`/reports/${id}`, { title }),
  listReportTemplates: () => apiClient.get<{ profiles: ReportProfile[] }>(`/reports/profiles`),
}
